/**
 * Region groups for Add Deployment drawer (Figma: accordion-based region selection).
 * Each group can be expanded to show radio options. Value is sent to deployV2 as
 * targetRegion (or gatewayRegion when value is "global").
 */
export interface RegionOption {
  key: string;
  value: string;
}

/** Case-insensitive match of option key/value against API deployment region strings */
export function isRegionAlreadyDeployed(
  option: RegionOption,
  deployedRegions: string[] | undefined
): boolean {
  if (!deployedRegions?.length) return false;
  const norm = (s: string) => s.trim().toLowerCase();
  const keys = new Set([norm(option.key), norm(option.value)]);
  return deployedRegions.some((dep) => keys.has(norm(dep)));
}

export interface RegionGroup {
  label: string;
  regions: RegionOption[];
}

/** Shape of each item from GET /v2/apis/regions */
export interface RegionItemFromApi {
  geography: string;
  targetRegion: string;
  provider?: string;
}

/** Filter regions to only those for the given provider (AWS | AZURE). Items without provider are excluded when a provider is specified. */
export function filterRegionsByProvider(
  items: RegionItemFromApi[],
  provider: "AWS" | "AZURE"
): RegionItemFromApi[] {
  const upper = provider.toUpperCase();
  return items.filter(
    (item) => item.provider && item.provider.toUpperCase() === upper
  );
}

/** Build region groups from API response: geography = group title, targetRegion = option value/label */
export function regionItemsToRegionGroups(items: RegionItemFromApi[]): RegionGroup[] {
  const byGeography = new Map<string, RegionOption[]>();
  for (const item of items) {
    const opt: RegionOption = { key: item.targetRegion, value: item.targetRegion };
    const list = byGeography.get(item.geography) ?? [];
    list.push(opt);
    byGeography.set(item.geography, list);
  }
  return Array.from(byGeography.entries()).map(([label, regions]) => ({ label, regions }));
}

export const REGION_GROUPS: RegionGroup[] = [
  {
    label: "North America",
    regions: [
      { key: "US East (N. Virginia)", value: "us-east-1" },
      { key: "US East (Ohio)", value: "us-east-2" },
      { key: "US West (N. California)", value: "us-west-1" },
      { key: "US West (Oregon)", value: "us-west-2" },
    ],
  },
  {
    label: "South America",
    regions: [
      { key: "São Paulo", value: "sa-east-1" },
    ],
  },
  {
    label: "Europe",
    regions: [
      { key: "EU West (Ireland)", value: "eu-west" },
      { key: "EU North (Stockholm)", value: "eu-north-1" },
    ],
  },
  {
    label: "Middle east & Africa",
    regions: [
      { key: "Middle East (Bahrain)", value: "me-south-1" },
      { key: "Africa (Cape Town)", value: "af-south-1" },
    ],
  },
  {
    label: "Asia Pacific",
    regions: [
      { key: "Asia Pacific (Singapore)", value: "ap-southeast-1" },
      { key: "Asia Pacific (Tokyo)", value: "ap-northeast-1" },
      { key: "Asia Pacific (Sydney)", value: "ap-southeast-2" },
    ],
  },
  {
    label: "Australia & New Zealand",
    regions: [
      { key: "Australia (Sydney)", value: "ap-southeast-2" },
      { key: "Australia (Melbourne)", value: "ap-southeast-4" },
    ],
  },
  {
    label: "Other",
    regions: [
      { key: "Global", value: "global" },
      { key: "Europe", value: "europe" },
      { key: "Asia Pacific", value: "asia-pacific" },
      { key: "Australia", value: "australia" },
      { key: "United Kingdom", value: "uk" },
    ],
  },
];

/** Flatten all region values for availability checks */
export function getAllRegionValues(): string[] {
  return REGION_GROUPS.flatMap((g) => g.regions.map((r) => r.value));
}

/** Get region display name by value (for deployV2 targetRegion when not global) */
export function getRegionDisplayName(value: string): string | undefined {
  for (const group of REGION_GROUPS) {
    const found = group.regions.find((r) => r.value === value);
    if (found) return found.key;
  }
  return undefined;
}

const OTHER_GROUP_LABEL = "Other";

/** Returns true if the given region value belongs to the "Other" group */
export function isRegionInOtherGroup(regionValue: string): boolean {
  const otherGroup = REGION_GROUPS.find((g) => g.label === OTHER_GROUP_LABEL);
  return otherGroup?.regions.some((r) => r.value === regionValue) ?? false;
}

/**
 * Map from target region (display name) to assigned gateway label.
 * Key = selected region label, Value = gateway description for "Assigned gateway" alert.
 */
const ASSIGNED_GATEWAY_BY_REGION: Record<string, string> = {
  // AWS - North America
  "US East (Ohio)":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "US East (N. Virginia)":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "US West (Oregon)":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "US West (N. California)":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',

  // AWS - Europe
  "Europe (Stockholm)": "North Europe",

  // AWS - Asia Pacific
  "Asia Pacific (Singapore)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',
  "Asia Pacific (Sydney)":
    'Multi regional gateway - "Australia East (Primary) Australia Southeast"',
  "Asia Pacific (Jakarta)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',
  "Asia Pacific (Melbourne)":
    'Multi regional gateway - "Australia East (Primary) Australia Southeast"',
  "Asia Pacific (Malaysia)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',
  "Asia Pacific (New Zealand)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',
  "Asia Pacific (Thailand)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',

  // Azure - Americas
  "East US":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "East US 2":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "South Central US":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "West US":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "West US 2":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "West US 3":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "West Central US":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',

  // Azure - Europe
  "North Europe": "North Europe",

  // Azure - Asia Pacific
  "Australia East":
    'Multi regional gateway - "Australia East (Primary) Australia Southeast"',
  "Australia Southeast":
    'Multi regional gateway - "Australia East (Primary) Australia Southeast"',
  "Central India":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',

  // Existing generic options
  Global:
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  Europe: "North Europe",
};

const DEFAULT_ASSIGNED_GATEWAY =
  'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"';

/** Get the assigned gateway label for the given target region display name (region key). */
export function getAssignedGatewayLabel(regionDisplayName: string): string {
  return ASSIGNED_GATEWAY_BY_REGION[regionDisplayName] ?? DEFAULT_ASSIGNED_GATEWAY;
}
